<template>
  <div id="dataset-modal">
    <div v-if="show" class="summary-bar" :class="`summary-${summaryStyle}`">
      <div class="row">
        <div class="col-xs-3 summary-col recap-list"
             @click="selectTab(0)" role="button">
          <div>
            <span :class="recap[0].classes" aria-hidden="true">
            </span>
            <strong><span v-html="recap[0].shortLabel"></span></strong>
          </div>
        </div>
        <div class="col-xs-3 summary-col recap-list"
             @click="selectTab(1)" role="button">
          <div>
            <span :class="recap[1].classes" aria-hidden="true">
            </span>
            <strong><span v-html="recap[1].shortLabel"></span></strong>
          </div>
          <div v-if="recap[0].valid && hasSheetDifferences"
               class="txt-warning">
            <span class="glyphicon glyphicon-warning-sign" aria-hidden="true">
            </span>
            <strong>Modifications détectées sur les fiches !</strong>
          </div>
        </div>
        <div class="col-xs-6 summary-col recap-list"
             @click="selectTab(2)" role="button">
          <div>
            <span :class="recap[2].classes" aria-hidden="true">
            </span>
            <strong><span v-html="recap[2].shortLabel"></span></strong>
          </div>
          <div class="row" v-if="recap[1].valid" >
            <div class="col-xs-6">
              <div>Taux de remplissage</div>
              <div class="progress">
                <div class="progress-bar bg-success" role="progressbar"
                     :style="progressBars.complete.style"
                     :aria-valuenow="progressBars.complete.value"
                     aria-valuemin="0"
                     aria-valuemax="100">
                  <strong>{{ progressBars.complete.value }}%</strong>
                </div>
              </div>
            </div>
            <div class="col-xs-6">
              <div>Taux d'alerte</div>
              <div class="progress">
                <div class="progress-bar"
                     :class="`bg-${progressBars.alert.value > 10 ? 'danger': 'warning'}`"
                     role="progressbar"
                     :style="progressBars.alert.style"
                     :aria-valuenow="progressBars.alert.value"
                     aria-valuemin="0"
                     aria-valuemax="100">
                  <strong>{{ progressBars.alert.value }}%</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <modal v-model="show"
           :appendToBody="appendToBody"
           :footer="false"
           class="main-modal"
           ref="modal"
           size="lg">
      <template slot="title">
        <span v-html="modalTitle"></span>
      </template>
      <div v-if="target != null">
        <alert type="info" v-if="!hasSheetDifferences">
          <p>
            <strong>
              Attention ! La mise à jour des fiches n'est pas automatique.
            </strong>
          </p>
          <p class="text-justify">
            Si vous ajoutez, supprimez ou modifiez une fiche correspondant à la
            période de surveillance, n'oubliez pas de mettre à jour les fiches
            pour «{{name}}» en utilisant le bouton plus bas dans cette page.
          </p>
        </alert>
        <alert type="warning" v-else>
          <p class="text-justify">
            Nous avons détecté des modifications sur vos fiches, pensez à les
            mettre à jour dans l'onglet
            <a @click="selectTab(1)" role="button"><strong>Fiches</strong></a>.
          </p>
        </alert>
      </div>
      <tabs v-model="currentTab">
        <tab title="Période">
          <h4>Période de surveillance</h4>
          <div class="form-group"
               :class="{ 'has-error': fields.name.error }">
            <label for="name" class="control-label">
              Nom
            </label>
            <input required
                   id="name"
                   class="form-control"
                   :disabled="nameDisabled"
                   v-model.trim="fields.name.value"
                   @blur="checkName"
                   type="text"
                   placeholder="Nom"/>
            <span class="text-danger help-block">{{ fields.name.error }}</span>
          </div>
          <alert type="info" v-if="target == null">
            <p class="text-justify">
              <strong>
                Merci de renseigner la (ou les) période(s) surveillées pour
                «{{name}}», puis de cliquer sur «Enregistrer les informations».
              </strong>
            </p>
          </alert>
          <div class="form-group">
            <label class="control-label">
              Période de surveillance
            </label>
            <div class="row">
              <div class="col-xs-12">
                <div>
                  <p>
                    <dropdown ref="dropdown">
                      <btn type="warning" class="dropdown-toggle">
                        <span v-if="fields.periodsByUfs === 0">
                          (Choisir la manière de remplir les périodes) &nbsp;&nbsp;
                        </span>
                        <span v-else-if="fields.periodsByUfs === 1">
                          Période de surveillance commune à tous les services
                        </span>
                        <span v-else>
                          Période de surveillance séparée par service
                        </span>
                        <span class="caret"></span>
                      </btn>
                      <template slot="dropdown">
                        <li>
                          <a role="button" @click="setPeriodsByUf(1)">
                            Période de surveillance commune à tous les services
                          </a>
                        </li>
                        <li v-if="module != 'bactADI' && surveillanceUfList.length > 1">
                          <a role="button" @click="setPeriodsByUf(2)">
                            Période de surveillance séparée par service
                          </a>
                        </li>
                      </template>
                    </dropdown>
                  </p>
                </div>
              </div>
            </div>
            <div v-for="(p, k) in fields.periods"
                 :key="`period-${k}`" class="panel panel-default">
              <div class="panel-body">
                <label class="control-label" v-if="p.name">
                  {{p.name}}
                </label>
                <sui-dataset-period v-model="p.value"
                                    :limits="periodLimits"
                                    :defaults="periodDefaults">
                </sui-dataset-period>
              </div>
            </div>
            <div class="row">
              <div class="col-xs-12">
                <sui-error-panel :error="error.info" v-if="error.info.error">
                </sui-error-panel>
                <p>
                  <btn type="primary" size="lg"
                       :disabled="saveDisabled" @click="save">
                    Enregistrer la période
                  </btn>
                </p>
              </div>
            </div>
          </div>
        </tab>
        <tab title="Fiches" v-if="versionDiff">
          <h4>Validation et envoi des fiches</h4>
          <div v-if="periodSheets.length === 0">
            <div v-if="target.step > 0">
              <alert type="success" >
                <p class="text-justify">
                  <strong>
                    Aucune infection n'a été retrouvée pour cette modalité.
                  </strong>
                </p>
              </alert>
            </div>
            <div v-else>
              <alert type="warning">
                <p class="text-justify">
                  <strong>
                    Il n'y a aucune fiche sur la période sélectionnée.
                  </strong>
                </p>
                <p class="text-justify"
                   v-if="module === 'bactADI' || module === 'expADI'">
                  Si vous n'avez eu aucune bactériémie pour cette modalité pendant
                  la période surveillée, merci d'utiliser le bouton ci-dessous.
                </p>
              </alert>
              <div v-if="module === 'bactADI' || module === 'expADI'">
                <btn @click="noSheets" type="primary" size="lg">
                  Aucune infection à déclarer sur la période surveillée
                </btn>
              </div>
            </div>
          </div>
          <div v-else>
            <div v-if="!hasSheetDifferences" >
              <alert v-if="periodSheets.length > 0" type="success">
                <p>
                  <strong>
                    Les données «{{name}}» contiennent {{periodSheets.length}}
                    fiche.s.
                  </strong>
                </p>
                <p>
                  Toutes les fiches sont à jour.
                </p>
                <p>
                  <btn size="sm" type="default"
                       @click="sheetNumbers = !sheetNumbers">
                    {{sheetNumbers ? 'Masquer' : 'Voir'}} la liste
                  </btn>
                </p>
                <collapse v-model="sheetNumbers">
                  <table class="table table-responsive">
                    <thead>
                      <tr>
                        <th>Numéro</th>
                        <th>Service</th>
                        <th>Uf</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(p, k) in displayPeriodSheets"
                          :key="`valide-sheet-${k}`">
                        <td>{{p.sheetNumber}}</td>
                        <td>{{p.unitType}}</td>
                        <td>{{p.uf}}</td>
                      </tr>
                    </tbody>
                  </table>
                </collapse>
              </alert>
              <alert v-else type="warning">
                Aucune fiche saisie.
              </alert>
            </div>
            <div v-else>
              <div v-if="!iterator.started">
                <alert type="warning" class="text-justify"
                       v-if="dsSheets == null || dsSheets.length === 0">
                  <p>
                    <strong>
                      Aucune fiche n'a été envoyée pour les données «{{name}}»,
                      mais il y a {{periodSheets.length}} fiche.s correspondant à
                      la période surveillée.
                    </strong>
                  </p>
                  <p>
                    Utilisez le bouton ci-dessous pour valider et envoyer
                    automatiquement les fiches.
                  </p>
                </alert>
                <alert type="warning" class="text-justify" v-else>
                  <p>
                    <strong>
                      Les données «{{name}}» contiennent actuellement
                      {{periodSheets.length}} fiche.s.
                    </strong>
                  </p>
                  <p>
                    Nous avons cependant détecté des modifications sur les fiches
                    depuis le dernier envoi :
                    <ul>
                      <li v-if="versionDiff.v.length > 0">
                        {{ versionDiff.v.length }} fiche.s modifiée.s
                      </li>
                      <li v-if="versionDiff.a.length > 0">
                        {{versionDiff.a.length}} fiche.s ajoutée.s
                      </li>
                      <li v-if="versionDiff.d.length > 0">
                        {{versionDiff.d.length}} fiche.s supprimée.s
                      </li>
                    </ul>
                    <strong>
                      Pensez à mettre à jour les fiches envoyées pour les données
                      «{{name}}» en utilisant le bouton ci-dessous.
                    </strong>
                  </p>
                </alert>
                <sui-error-panel :error="error.sheets" v-if="error.sheets.error">
                </sui-error-panel>
                <p>
                  <btn @click="updateSheets" type="primary" size="lg">
                    Mettre à jour les fiches pour «{{ name }}»
                  </btn>
                </p>
              </div>
              <div v-else>
                <steps :steps="iterator.steps" :stepStates="iterator.stepStates"></steps>
              </div>
            </div>
          </div>
        </tab>
        <tab title="Dénominateurs"
             v-if="versionDiff && dsSheets != null &&
                    (dsSheets.length > 0 || target.step > 0)">
          <h2>Dénominateurs sur la période surveillée</h2>
          <alert type="success" v-if="target.step === 2">
            <p class="text-justify">
              <strong>
                Tous les dénominateurs sont enregistrés.
              </strong>
            </p>
          </alert>
          <alert type="warning" v-else-if="target.step === 1">
            <p class="text-justify">
              Merci de saisir les données d'activité correspondant à la
              période surveillée.
            </p>
          </alert>
          <div v-if="module !== 'bactADI'">
            <btn v-if="debug" type="danger" @click="dbgFillUnitList">
              <span class="glyphicon glyphicon-wrench" aria-hidden="true"></span>
              Remplir
            </btn>
            <div v-for="(u, k) in unitList" :key="`unitlist-${u.code}-${k}`">
              <unit-denominators
                v-model="u.denominators"
                :optional="u.optional"
                :columns="u.columns"
                :header="u.header"
                :checker="u.checker"
                :code="u.code"
                :module="module"
                @progress="progressUpdateOrAdd">
              </unit-denominators>
            </div>
          </div>
          <div v-else-if="discList">
            <div v-if="dsSheets.length > 0">
              <div v-if="discWithBact.length > 0">
                <h3>Disciplines surveillées avec bactériémie</h3>
                <h4>Choix des services surveillés</h4>
                <div class="row">
                  <div v-for="(e, k) in discWithBact" :key="`sheet-${k}`"
                       class="col-xs-6 col-md-4 discipline-selector">
                    <c-checkbox :type="module"
                                  :disabled="true"
                                  block
                                  size="sm" :value="true">
                      {{ e.code }}
                      <small v-if="e.subtitle"> ({{ e.subtitle }})</small>
                    </c-checkbox>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <br>
              <div class="well well-sm"
                   :class="`well-${discAlerts[u.code] != null ? 'danger' : 'default'}`"
                   v-for="u in discList.list" :key="`unitlist-${u.code}`">
                <div class="row">
                  <div class="col-xs-12">
                    <h3>
                      Dénominateurs
                      <strong>
                        {{u.code}}
                        <span v-if="u.disc.subtitle"> ({{ u.disc.subtitle }})</span>
                      </strong>
                    </h3>
                    <hr align="left" class="category-den-sep">
                      <h4>Dénominateurs obligatoires, tous services surveillés confondus (avec ou sans bactériémies)</h4>

                  </div>
                </div>
                <btn v-if="debug" type="danger" @click="dbgFill">
                  <span class="glyphicon glyphicon-wrench" aria-hidden="true"></span>
                  Remplir
                </btn>
                <unit-denominators
                  v-model="u.denominators"
                  :optional="u.optional"
                  :columns="u.columns"
                  :header="u.header"
                  :checker="u.checker"
                  :code="u.code"
                  :module="module"
                  @progress="progressUpdateOrAdd">
                </unit-denominators>
                <div class="row">
                  <div class="col-xs-12"
                       v-if="hasUfs[u.code] || hasOptionals[u.code]">
                    <hr align="left" class="category-den-sep">
                    <h4>Dénominateurs optionnels</h4>
                  </div>
                  <div class="col-xs-6 button-list"
                       v-if="hasUfs[u.code]">
                    <c-checkbox :type="module" @input="toggleUfCollapse(u.code)"
                                block size="sm" :value="ufCollapse[u.code]">
                      UFs présentant au moins une bactériémie<br>
                      <small>Pour obtenir des résultats locaux par UF</small>
                    </c-checkbox>
                  </div>
                  <div class="col-xs-6 button-list"
                       v-if="hasOptionals[u.code]">
                    <c-checkbox type="temporary" @input="toggleOptCollapse(u.code)"
                                block size="sm" :value="optCollapse[u.code]">
                      Spécialité
                      <span v-for="o in discList.optionals[u.code]"
                             :key="`opt-label-${u.code}-${o.code}`">{{ o.code }}</span>
                      (avec ou sans bactériémies)
                      <br><small>Pour obtenir des résultats détaillés pour cette spécialité</small>
                    </c-checkbox>
                  </div>
                </div>

                <div v-if="hasUfs[u.code]">
                  <div class="row">
                    <div class="col-xs-12">
                      <collapse v-model="ufCollapse[u.code]">
                        <div class="button-list row">
                          <div v-for="(e, k) in ufList[u.code]" :key="`bact-uf-${u.code}-${k}`"
                               class="col-xs-6 col-md-3">
                            <c-checkbox :type="module" @input="toggleUf($event, e)"
                                    block size="xs" :value="e.selected">
                              {{ e.unit.uf }}<br><small>{{ e.unit.code }}</small>
                            </c-checkbox>
                          </div>
                          <div class="col-xs-6 col-md-3">
                            <btn block class="select-button"
                                 @click="selectListedUf(u.code, !allUfsSelected[u.code])">
                              <span v-if="allUfsSelected[u.code]">
                                Tout désélectionner
                              </span>
                              <span v-else>
                                Tout sélectionner
                              </span>
                            </btn>
                          </div>
                        </div>
                        <div  v-if="discAlerts[u.code] != null">
                          <br>
                          <alert type="danger">
                            <p>
                              <strong>
                                La somme des valeurs des Ufs est supérieure à la valeur de la discipline pour :
                                <ul>
                                  <li v-for="(a, k) in discAlerts[u.code]"
                                      :key="`disc-${u.code}-alert-${k}`">
                                    {{ a }}
                                  </li>
                                </ul>
                              </strong>
                            </p>
                          </alert>
                        </div>

                        <div v-for="(v, l) in displayedUfList[u.code]"
                             :key="`uf-denominators-${v.code}-${v.uf}-${l}`">
                          <unit-denominators
                            v-model="v.denominators"
                            @input="computeDenInterDiscAlerts"
                            :optional="v.optional"
                            :columns="v.columns"
                            :header="v.header"
                            :checker="v.checker"
                            :code="u.code"
                            :module="module">
                          </unit-denominators>
                        </div>
                      </collapse>
                    </div>
                  </div>
                </div>
                <div v-if="hasOptionals[u.code]">
                  <div class="row">
                    <div class="col-xs-12">
                      <collapse v-model="optCollapse[u.code]">
                        <div v-for="(v, l) in discList.optionals[u.code]"
                             :key="`uf-denominators-opt-${v.code}-${l}`">
                          <unit-denominators
                            v-model="v.denominators"
                            :columns="v.columns"
                            :header="v.header"
                            :checker="v.checker"
                            :code="u.code"
                            :module="module">
                          </unit-denominators>
                        </div>
                      </collapse>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="discList.missing.length > 0">
              <h3>
                Disciplines surveillées sans bactériémie
              </h3>
              <p class="text-justify">
                Merci de sélectionner les disciplines surveillées qui n'ont pas
                eu de bactériémies. <br>Vous pourrez ensuite saisir les dénominateurs correspondants ci-dessous.
              </p>
              <div class="row">
                <div v-for="(e, k) in missingList" :key="`missing-${k}`"
                     class="col-xs-6 col-md-4 discipline-selector">
                  <c-checkbox :type="module" @input="toggleDisc(e.disc)"
                                block size="sm" :value="e.selected">
                    {{ e.display.code }}
                    <small v-if="e.display.subtitle"> ({{ e.display.subtitle }})</small>
                  </c-checkbox>
                </div>
              </div>
            </div>
            <br>
            <div v-if="discList.empty.length > 0">
              <btn v-if="debug" type="danger" @click="dbgFillEmpty">
                <span class="glyphicon glyphicon-wrench" aria-hidden="true"></span>
                Remplir
              </btn>
              <div class="well well-sm" v-if="emptyDiscList.length > 0">
                <div v-for="u in discList.empty"
                     :key="`empty-ud-${u.code}`">
                  <unit-denominators
                    v-model="u.denominators"
                    :optional="u.optional"
                    :columns="u.columns"
                    :header="u.header"
                    :checker="u.checker"
                    :code="u.code"
                    :module="module"
                    @progress="progressUpdateOrAdd">
                  </unit-denominators>
                </div>
                <div v-if="discList.emptyOptionals">
                  <div v-for="u in discList.empty"
                       :key="`empty-op-${u.code}`">
                    <div v-for="(o, j) in discList.emptyOptionals[u.code]"
                         :key="`empty-${u.code}-${j}`">
                      <unit-denominators
                        v-model="o.denominators"
                        :optional="true"
                        :columns="o.columns"
                        :header="o.header"
                        :checker="o.checker"
                        :code="o.code"
                        :module="module">
                      </unit-denominators>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="module === 'expADI'">
            <hr />
            <h3>
              Tableaux d'exposition
              <label class="label label-expADI">Exp ADI</label>
            </h3>
            <alert type="warning" v-if="target.step == 1">
              <p class="text-justify">
                <strong>
                  Merci de bien vouloir renseigner tous les dénominateurs
                  avant de sauvegarder à l'aide du bouton en bas de page.
                </strong>
              </p>
              <p class="text-justify">
                Pour chaque service, vous pouvez sélectionner le tableau
                d'exposition correspondant en utilisant le sélecteur sur
                la droite de l'encart.
              </p>
              <p class="text-justify">
                Pour chaque dénominateur, additionnez toutes les valeurs
                correspondant à la période surveillée.
              </p>
            </alert>
            <alert v-else type="success">
              <p class="text-justify">
                <strong>
                  Tous les dénominateurs sont enregistrés.
                </strong>
              </p>
            </alert>
            <div v-for="(u, k) in unitList" :key="`exp-ud-${u.code}-${k}`">
              <exp-array v-model="u.expDenominators" :year="surveillance.year"
                         :uf="u.uf" :code="u.code" :module="module"
                         @progress="progressUpdateOrAdd"
                         @array="updateExpArray(u, $event)">
              </exp-array>
              <hr />
            </div>
            <div v-if="optionalListDisplay.length > 0">
              <h3>
                Modules optionnels
                <label class="label label-expADI">Exp ADI</label>
              </h3>
              <alert type="warning">
                <p class="text-justify">
                  Les fiches saisies ne nous permettent pas de déduire
                  toutes les surveillances effectuées. Merci de répondre aux
                  questions ci-dessous.
                </p>
              </alert>
              <div v-for="(u, k) in optionalListDisplay" :key="`exp-op-${k}`">
                <h4>{{ u.op.unit.uf }} ({{ u.op.unit.code }})</h4>
                <div class="row">
                  <div :key="`exp-op-${k}`" class="col-xs-12 col-sm-9 col-md-6">
                    <p>{{ u.op.opt.title }}</p>
                    <div class="row">
                      <div class="col-xs-6">
                        <c-checkbox :type="module"
                              block
                              size="sm"
                              :value="u.yes"
                              @input="setOptional(u.op.opt.key, u.op.code, true, $event)">
                          Oui
                        </c-checkbox>
                      </div>
                      <div class="col-xs-6">
                        <c-checkbox :type="module"
                              size="sm"
                              block
                              :value="u.no"
                              @input="setOptional(u.op.opt.key, u.op.code, false, $event)">
                          Non
                        </c-checkbox>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <hr />
            </div>
          </div>
          <sui-error-panel :error="error.denom" v-if="error.denom.error">
          </sui-error-panel>
          <alert v-if="denInterDiscAlert.alert" type="danger">
            <p v-if="denInterDiscAlert.duplicate.length > 0">
              Des dénominateurs identiques ont été saisi pour :
              <ul>
                <li v-for="(d, k) in denInterDiscAlert.duplicate"
                    :key="`duplicate-${k}`">
                  <strong>{{ d.first }}</strong> et <strong>{{ d.second }}</strong>
                </li>
              </ul>
              <strong>Merci de vérifier la saisie.</strong>
            </p>
          </alert>

          <div class="row" v-if="shouldConfirmNoneMissing">
            <div class="col-xs-12">
              <p>
                <btn type="warning" @click="confirmNoneMissing"
                     size="lg">
                  Je n'ai surveillé aucune autre discipline
                </btn>
              </p>
            </div>
          </div>
          <div v-else-if="allowNoMissing">
            <alert type="warning">
              <p>
                <strong>Aucune discipline sans bactériémie n'a été surveillée.</strong>
              </p>
            </alert>
          </div>
          <div class="update-button" v-show="!shouldConfirmNoneMissing">
            <btn size="lg" @click="updateDenominators" type="primary">
              Enregistrer les dénominateurs
            </btn>
          </div>
        </tab>
      </tabs>
    </modal>
  </div>
</template>
<script>
import suiErrorPanel from '@/sui/components/errorpanel'
import expArray from '@/sui/components/expArray'
import unitDenominators from '@/sui/components/unitDenominators'
import steps from '@/sui/components/steps'
import period from '@/sui/components/dataset/period'
import checkbox from '@/sui/components/checkbox'

import modules from '@/sui/data/modules'

import StepIterator from '@/sui/tools/stepiterator'
import units from '@/sui/data/units'

import Error from '@/sui/tools/error'
import { confirm } from '@/sui/tools/modal'

import impl from '@/sui/components/impl/dataset'

import moment from 'moment'

export default {
  name: 'dataset-modal',
  props: {
    appendToBody: {
      type: Boolean
    },

    value: {
      type: Boolean,
      required: true
    },

    surveillance: {
      type: Object,
      required: true
    },

    name: {
      type: String
    },

    module: {
      type: String,
      required: true
    }
  },

  components: {
    'sui-error-panel': suiErrorPanel,
    'sui-dataset-period': period,
    'steps': steps,
    'unit-denominators': unitDenominators,
    'c-checkbox': checkbox,
    'exp-array': expArray
  },

  data: function () {
    return {
      denInterDiscAlert: {},
      currentTab: 0,
      ufCollapse: {},
      optCollapse: {},
      denInfo: {},
      totalDen: 0,
      prg: {
        all: 0,
        miss: 0,
        alert: 0,
        completePercent: 0,
        alertPercent: 0
      },
      noneMissingConfirmed: false,
      dsDenominators: null,
      show: this.value,
      sheetNumbers: false,
      error: {
        info: new Error(),
        denom: new Error(),
        sheets: new Error()
      },
      waiting: false,
      iterator: new StepIterator(impl.steps),
      dsSheets: null,
      periodSheets: null,
      periodSheetsAsked: false,
      fields: {
        name: { value: this.name, error: '' },
        periodsByUfs: 0,
        periods: []
      },
      filled: null,
      emptyDiscList: [],
      selectedUfList: [],
      optional: {
        seps: [],
        pneu: []
      }
    }
  },

  watch: {
    hasSheetDifferences: function () {
      if (this.hasSheetDifferences) {
        this.currentTab = 1
      }
    },

    name: function () {
      this.filled = null
    },

    allowNoMissing: function (val) {
      if (!val) {
        this.noneMissingConfirmed = false
      }
    },

    value: function () {
      if (this.value && this.filled == null) {
        this.fill()
      }
      this.show = this.value
    },

    show: function (s) {
      if (!s) {
        if (this.iterator.completed) {
          const request = {
            t: this.module,
            s: this.surveillance.id,
            y: this.surveillance.year
          }
          this.$store.dispatch('sheet/load', request)
        }
      }
      this.$emit('input', this.show)
    },

    surveillance: function (s) {
      this.refill()
    },

    module: function () {
      this.refill()
    },

    discList: function (value) {
      if (value == null) {
        return
      }

      for (let key in this.discList.optionals) {
        if (key in this.optCollapse) {
          continue
        }
        let initial = false
        const ops = this.discList.optionals[key]
        for (let i = 0; i !== this.unitList.length; ++i) {
          const ul = this.unitList[i]
          if (ops.find(op => op.code === ul.code) != null) {
            initial = true
            break
          }
        }

        if (!initial) {
          for (let i = 0; i !== ops.length; ++i) {
            const op = ops[i]
            if (this.dsDenominators.find(den => den.ut === op.unitType) != null) {
              initial = true
              break
            }
          }
        }

        this.$set(this.optCollapse, key, initial)
      }
    },

    ufList: function (value) {
      if (value == null) {
        return
      }

      for (let key in this.ufList) {
        if (key in this.ufCollapse) {
          continue
        }

        const ufs = this.ufList[key]
        let initial = false
        for (let i = 0; i !== ufs.length; ++i) {
          const uf = ufs[i]
          if (uf.selected) {
            initial = true
            break
          }
        }
        this.$set(this.ufCollapse, key, initial)
      }
    },

    unitList: function (value) {
      if (this.filled === 'bactADI' ||
          value == null ||
          value.length === 0 ||
          this.periodSheetsAsked) {
        return
      }

      const vm = this
      const p = vm.target.peri
      if (p.length > 0 && p[0].unit != null && p[0].unit !== '') {
        vm.fields.periodsByUfs = 2

        const byUnit = {}
        p.forEach(
          pu => {
            if (byUnit[pu.unit] == null) {
              byUnit[pu.unit] = [pu]
            } else {
              byUnit[pu.unit].push(pu)
            }
          }
        )
        vm.fields.periods.length = 0
        const periods = []
        for (let u in byUnit) {
          const l = byUnit[u]
          const s = u.split('.')
          if (s.length !== 2) {
            vm.error.set(
              'Erreur de donnée !',
              `Une erreur innatendue s'est produite lors de l'extraction
                des périodes par ufs.`,
              `Merci de nous contacter via le formulaire de contact.`
            )
            return
          }
          const ut = parseInt(s[0])
          const uf = s[1]
          const unit = vm.unitList.find(
            i => i.uf === uf && i.unitType === ut
          )

          if (unit == null) {
            continue
          }

          periods.push(
            {
              name: `${unit.uf} (${unit.code})`,
              value: impl.buildPeriod(vm.surveillance.year, l, u)
            }
          )
        }
        vm.fields.periods = periods
        this.getPeriodSheets()
      }
    }
  },

  mounted: function () {
    this.refill()
  },

  computed: {
    hasSheetDifferences: function () {
      return this.versionDiff && this.versionDiff.diffCount > 0
    },

    discAlerts: function () {
      const alerts = this.denInterDiscAlert.ufNotDisc
      if (this.discList == null || alerts == null || alerts.length === 0) {
        return {}
      }

      const result = {}
      const list = this.discList.list
      for (let i = 0; i !== list.length; ++i) {
        const disc = list[i]
        const al = alerts[disc.code]
        if (al && al.length > 0) {
          const dl = []
          for (let j = 0; j !== al.length; ++j) {
            const den = al[j]
            dl.push(disc.columns.find(c => c.key === den).title)
          }
          result[disc.code] = dl
        }
      }
      return result
    },

    debug: function () {
      return process.env.VUE_APP_SPIADI_DEBUG === '1'
    },

    hasUfs: function () {
      if (this.discList == null) {
        return null
      }

      const result = {}
      for (let i = 0; i !== this.discList.list.length; ++i) {
        const u = this.discList.list[i]
        result[u.code] = this.ufList && this.ufList[u.code] && this.ufList[u.code].length > 0
      }
      return result
    },

    hasOptionals: function () {
      if (this.discList == null) {
        return null
      }

      const result = {}
      for (let i = 0; i !== this.discList.list.length; ++i) {
        const u = this.discList.list[i]
        result[u.code] = this.discList.optionals[u.code] && this.discList.optionals[u.code].length > 0
      }
      return result
    },

    periodLimits: function () {
      const spiadi = this.name.toLowerCase().includes('spiadi')
      const y = this.surveillance.year
      return spiadi ? impl.mainPeriodLimits(y) : impl.customPeriodLimits(y)
    },

    periodDefaults: function () {
      const y = this.surveillance.year
      return {
        start: `${y}-01-01`,
        end: `${y}-04-01`
      }
    },

    recap: function () {
      return impl.datasetRecap(this)
    },

    summaryStyle: function () {
      for (let i = 0; i !== this.recap.length; ++i) {
        if (!this.recap[i].valid) {
          return 'warning'
        }
      }

      if (this.hasSheetDifferences) {
        return 'warning'
      }

      return 'success'
    },

    displayPeriodSheets: function () {
      const year = `${this.surveillance.year}`
      const mod = modules[year][this.module]
      return impl.makePeriodSheetsList(year, this.periodSheets, mod)
    },

    periods: function () {
      return impl.toServerPeriods(this.fields.periods)
    },

    moduleName: function () {
      return modules[`${this.surveillance.year}`][this.module].label
    },

    surveillanceUfList: function () {
      return this.surveillance.getUfs(this.module)
    },

    unitList: function () {
      if (this.filled == null || this.dsSheets == null) {
        return []
      }

      const denominators = []
      if (this._computedWatchers.unitList.value != null) {
        this._computedWatchers.unitList.value.forEach(
          u => {
            denominators.push({ ut: u.ut, uf: u.uf, value: u.denominators })
          }
        )
      }

      const den = {
        remote: this.dsDenominators,
        edit: denominators
      }
      const result = impl.extractUnitList(
        this.surveillance.year, this.dsSheets, den, this.surveillance,
        this.module, this.periods
      )

      return result
    },

    optionalList: function () {
      return impl.extractOptionalList(
        this.surveillance.year, this.unitList
      )
    },

    optionalListDisplay: function () {
      const result = []
      for (let i in this.optionalList) {
        const op = this.optionalList[i]

        const selected = this.optional[op.opt.key].find(
          o => o.code === op.code
        )

        if (selected != null && selected.forced) {
          continue
        }

        const r = {
          op: op,
          yes: selected != null && selected.value,
          no: selected != null && !selected.value
        }

        result.push(r)
      }
      return result
    },

    displayedUfList: function () {
      return impl.prepareUfList(this.ufList)
    },

    discList: function () {
      if (this.dsSheets == null ||
          this.filled !== 'bactADI') {
        return null
      }

      const edit = []
      if (this._computedWatchers.discList.value != null) {
        const v = this._computedWatchers.discList.value
        v.list.forEach(
          u => {
            edit.push({ ut: u.unitType, uf: u.uf, value: u.denominators })
          }
        )
        v.empty.forEach(
          u => {
            edit.push({ ut: u.unitType, uf: u.uf, value: u.denominators })
          }
        )
      }

      const result = impl.extractDiscList(this, edit)
      return result
    },

    discWithBact: function () {
      if (this.discList == null) {
        return null
      }

      const result = []
      this.discList.list.forEach(
        l => {
          if (result.indexOf(l.disc) === -1) {
            result.push(l.disc)
          }
        }
      )
      return result
    },

    versionDiff: function () {
      const t = this.target
      if (t == null || !this.periodSheets || !this.dsSheets) {
        return null
      }

      return impl.versionDiff(this.dsSheets, this.periodSheets)
    },

    saveDisabled: function () {
      let same = this.target && this.periods.length === this.target.peri.length
      if (same) {
        const pred = (f, s) => {
          const unit = f.unit.localeCompare(s.unit)
          if (unit !== 0) {
            return unit
          }

          const start = moment(f.start).valueOf() - moment(s.start).valueOf()
          if (start !== 0) {
            return start
          }

          return moment(f.end).valueOf() - moment(s.end).valueOf()
        }
        const tp = this.target.peri.slice().sort(pred)
        const p = this.periods.slice().sort(pred)
        for (let i in tp) {
          if (pred(tp[i], p[i]) !== 0) {
            same = false
            break
          }
        }
      }
      return this.waiting || this.hasError() || same
    },

    modalTitle: function () {
      const e = this.surveillance.establishment.name
      const m = this.moduleName
      const l = `label-${this.module}`
      return `${e}<br>Clôture <span class='label ${l}'>${m}</span> pour «${this.name}»`
    },

    nameDisabled: function () {
      return this.name != null || this.target != null
    },

    target: function () {
      if (this.surveillance == null) {
        return null
      }

      const mod = this.surveillance.modules[this.module]
      if (mod.dset == null) {
        return null
      }

      return mod.dset.find(
        d => d.name === this.fields.name.value
      )
    },

    reference: function () {
      if (this.surveillance == null) {
        return null
      }

      return modules.get(this.surveillance.year)[this.module].sheets.edit
    },

    ufList: function () {
      if (this.dsSheets == null || this.discList == null) {
        return null
      }

      return impl.extractUfList(this.year, this.unitList, this.discList.list, this.selectedUfList)
    },

    allUfsSelected: function () {
      if (this.ufList == null) {
        return null
      }

      const result = {}
      for (let key in this.ufList) {
        const ufs = this.ufList[key]
        let all = true
        for (let i = 0; i !== ufs.length; ++i) {
          const uf = ufs[i]
          if (!uf.selected) {
            all = false
            break
          }
        }
        result[key] = all
      }
      return result
    },

    missingList: function () {
      const result = []
      this.discList.missing.forEach(
        d => {
          const found = this.emptyDiscList.find(
            e => e.unitType === d.unitType
          )
          result.push(
            {
              selected: found != null,
              disc: d,
              display: d.disc
            }
          )
        }
      )
      return result
    },

    allowNoMissing: function () {
      return this.discList != null &&
             this.unitList != null &&
             this.discList.empty.length === 0 &&
             this.unitList.length > 0
    },

    shouldConfirmNoneMissing: function () {
      return this.allowNoMissing && !this.noneMissingConfirmed
    },

    progressBars: function () {
      let complete = this.prg.completePercent
      if (this.shouldConfirmNoneMissing) {
        complete = complete / 2
      }

      return {
        complete: {
          style: `width: ${complete}%`,
          value: complete
        },

        alert: {
          style: `width: ${this.prg.alertPercent}%`,
          value: this.prg.alertPercent
        }
      }
    }
  },

  methods: {
    computeDenInterDiscAlerts: function () {
      if (this.discList == null) {
        return
      }

      const duplicate = []
      const ufNotDisc = {}

      const keys = {}
      const list = [...this.discList.list, ...this.discList.empty]

      for (let i = 0; i !== list.length; ++i) {
        const disc = list[i]
        if (Object.values(disc.denominators).find(d => d !== '') != null) {
          const key = JSON.stringify(disc.denominators)
          const code = keys[key]
          if (code != null) {
            duplicate.push({ first: code, second: disc.code })
          } else {
            keys[key] = disc.code
          }
        }

        const dufs = this.displayedUfList[disc.code]
        if (dufs != null && dufs.length > 0) {
          const ufs = dufs.filter(
            u => this.selectedUfList.find(s => s.uf === u.uf && u.unitType === s.ut)
          )
          const errors = []
          if (ufs.length > 0) {
            const denominators = Object.keys(disc.denominators)
            for (let d = 0; d !== denominators.length; ++d) {
              const den = denominators[d]
              const dval = parseInt(disc.denominators[den])
              if (isNaN(dval) || dval < 0) {
                continue
              }

              let sum = 0
              for (let j = 0; j !== ufs.length; ++j) {
                const uf = ufs[j]
                const ufval = parseInt(uf.denominators[den])
                if (!isNaN(ufval)) {
                  sum += ufval
                }
              }
              if (sum > dval) {
                errors.push(den)
              }
            }
          }
          ufNotDisc[disc.code] = errors
        }
      }

      const result = {
        alert: duplicate.length > 0 || ufNotDisc.length > 0,
        duplicate: duplicate,
        ufNotDisc: ufNotDisc
      }
      this.denInterDiscAlert = result
    },

    confirmNoneMissing: function () {
      confirm(
        this,
        `<p class="text-justify">
           Êtes-vous sûr.e de n'avoir surveillé aucune autre discipline ?
         </p>`
      ).then(() => {
        this.noneMissingConfirmed = true
      }).catch((e) => {})
    },

    selectTab: function (tab) {
      if (this.target == null || tab > this.target.step + 1) {
        return
      }
      this.currentTab = tab
    },

    toggleUfCollapse: function (code) {
      this.ufCollapse[code] = !this.ufCollapse[code]
      this.selectListedUf(code, this.ufCollapse[code])
    },

    toggleOptCollapse: function (code) {
      this.optCollapse[code] = !this.optCollapse[code]
    },

    computeProgress: function () {
      const total = this.prg.all
      const completePercent = Math.round(((total - this.prg.miss) / total) * 100)
      this.prg.completePercent = completePercent
      const alertPercent = Math.round((this.prg.alert / total) * 100)
      this.prg.alertPercent = alertPercent
    },

    removeDiscProgress: function (key) {
      const curr = this.denInfo[key]
      if (curr != null) {
        this.prg.all -= curr.all
        this.prg.miss -= curr.miss
        this.prg.alert -= curr.alert
        this.computeProgress()

        delete this.denInfo[key]
      }
    },

    progressUpdateOrAdd: function (counts) {
      if (counts.total == null &&
          counts.missing == null &&
          counts.alert == null) {
        this.removeDiscProgress(counts.key)
        return
      }
      if (counts.key in this.denInfo) {
        const curr = this.denInfo[counts.key]
        if (curr.all !== counts.total ||
            curr.miss !== counts.missing ||
            curr.alert !== counts.alert ||
            Object.keys(this.denInfo).length !== this.totalDen) {
          this.prg.all -= (curr.all - counts.total)
          this.prg.miss -= (curr.miss - counts.missing)
          this.prg.alert -= (curr.alert - counts.alert)
          this.computeProgress()

          this.$set(this.denInfo, counts.key, { alert: counts.alert, miss: counts.missing, all: counts.total })
          this.totalDen = Object.keys(this.denInfo).length
        }
      } else {
        this.$set(this.denInfo, counts.key, { alert: counts.alert, miss: counts.missing, all: counts.total })
        this.prg.all += counts.total
        this.prg.miss += counts.missing
        this.prg.alert += counts.alert
        this.computeProgress()

        this.totalDen = Object.keys(this.denInfo).length
      }
      this.computeDenInterDiscAlerts()
    },

    dbgFillUnitList: function () {
      impl.dbgFillList(this, this.unitList)
      this.dbgFillUfs()
    },

    dbgFill: function () {
      impl.dbgFillList(this, this.discList.list)
      this.dbgFillUfs()
      this.dbgFillOptionals()
    },

    dbgFillEmpty: function () {
      const list = []
      for (let i = 0; i !== this.discList.empty.length; ++i) {
        const disc = this.discList.empty[i]
        list.push(disc)
        const ops = this.discList.emptyOptionals[disc.code]
        if (ops) {
          list.push(...ops)
        }
      }

      impl.dbgFillList(this, list)
    },

    dbgFillOptionals: function () {
      const result = []
      const l = this.discList.optionals
      for (let key in l) {
        result.push(...l[key])
      }

      impl.dbgFillList(this, result)
    },

    dbgFillUfs: function () {
      const result = []
      if (this.discList != null) {
        const l = this.discList.list
        for (let i = 0; i !== l.length; ++i) {
          const ufs = this.ufList[l[i].code]
          if (ufs == null) {
            continue
          }

          for (let j = 0; j !== ufs.length; ++j) {
            const uf = ufs[j]
            if (uf.selected) {
              result.push(uf.unit)
            }
          }
        }
      }

      impl.dbgFillList(this, result)
    },

    updateExpArray: function (u, s) {
      if (s.old == null) {
        s.new.forEach(
          d => {
            if (u.expDenominators[d] === undefined) {
              u.expDenominators[d] = null
            }
          }
        )
        return
      }

      const save = {}
      s.old.forEach(
        d => {
          save[d] = u.expDenominators[d]
          delete u.expDenominators[d]
        }
      )
      s.new.forEach(
        d => {
          if (save[d] == null) {
            if (u.denominators[d] != null) {
              u.expDenominators[d] = u.denominators[d]
            } else {
              u.expDenominators[d] = null
            }
          } else {
            u.expDenominators[d] = save[d]
          }
        }
      )
    },

    setOptional: function (key, code, value, event) {
      if (!event) {
        return
      }

      const op = this.optional[key]
      const i = op.findIndex(o => o.code === code)
      if (i === -1) {
        op.push({ code: code, value: value })
      } else {
        op[i].value = value
      }
    },

    hasError: function () {
      if (this.fields.name.error !== '') {
        return true
      }

      for (let i in this.fields.periods) {
        const p = this.fields.periods[i]
        if (p.value.date.error !== '' ||
            p.value.date2.error !== '') {
          return true
        }
      }
      return false
    },

    toggleDisc: function (d) {
      const found = this.emptyDiscList.find((element) => element.id === d.id && element.code === d.code)
      if (found == null) {
        this.emptyDiscList.push(d)
      } else {
        this.emptyDiscList.splice(this.emptyDiscList.indexOf(found), 1)
        this.removeDiscProgress(d.code)
      }
    },

    toggleUf: function (event, uf) {
      if (uf.selected) {
        this.removeDiscProgress(`${uf.unit.code}-${uf.unit.uf}`)
      }
      if (event != null && event === uf.selected) {
        return
      }
      const d = uf.unit
      const i = this.selectedUfList.findIndex(
        u => d.uf === u.uf && d.unitType === u.ut
      )
      if (i === -1) {
        this.selectedUfList.push(
          { uf: d.uf, ut: d.unitType }
        )
      } else {
        this.selectedUfList.splice(i, 1)
      }
      this.computeDenInterDiscAlerts()
    },

    selectListedUf: function (code, target) {
      const list = this.ufList[code]
      for (let i = 0; i !== list.length; ++i) {
        const e = list[i]
        if (e.selected !== target) {
          this.toggleUf(null, e)
        }
      }
    },

    refill: function () {
      if (this.value) {
        this.fill()
      } else {
        this.clean()
        this.filled = null
      }
    },

    updateDenominators: function () {
      const denom = []
      const b = this.filled === 'bactADI'
      const exp = this.filled === 'expADI'
      const otherList = []
      if (b) {
        for (const key in this.displayedUfList) {
          otherList.push(...this.displayedUfList[key])
        }

        for (const key in this.discList.optionals) {
          if (this.optCollapse[key]) {
            otherList.push(...this.discList.optionals[key])
          }
        }

        for (let i = 0; i !== this.discList.empty.length; ++i) {
          const disc = this.discList.empty[i]
          const ops = this.discList.emptyOptionals[disc.code]
          if (ops) {
            otherList.push(...ops)
          }
        }
      }

      const lists = b ? [
        otherList,
        this.discList.list,
        this.discList.empty
      ] : [
        this.unitList
      ]

      const vm = this
      for (let i in lists) {
        const l = lists[i]
        if (!impl.listDenominators(l, denom, exp)) {
          this.error.denom.set(
            'Dénominateurs manquants !',
            `Merci de remplir tous les dénominateurs avant d'enregistrer.`,
            ``,
            false
          )
          return
        }
      }

      if (denom.length === 0) {
        this.error.denom.set(
          'Dénominateurs vides !',
          `Merci d'ajouter les dénominateurs pour au moins une discipline avant
          d'enregistrer.`,
          `Vous pouvez utiliser le bouton ci-dessus pour ajouter les
dénominateurs d'une discipline surveillée pour lequel il n'y a pas eu de
bactériémies.`,
          false
        )
        return
      }

      const optional = {
        pneumopathies: null,
        sepsis: null
      }

      if (exp) {
        const l = this.optional.pneu.length + this.optional.seps.length
        if (l < this.optionalListDisplay.length) {
          this.error.denom.set(
            'Modules optionnels manquants',
            `Merci de répondre «Oui» ou «Non» pour toutes les questions
concernant les modules optionnels.`,
            ``,
            false
          )
          return
        }
        optional.pneumopathies = []
        this.optional.pneu.forEach(
          p => {
            if (p.value) {
              optional.pneumopathies.push(p.code)
            }
          }
        )
        optional.sepsis = []
        this.optional.seps.forEach(
          p => {
            if (p.value) {
              optional.sepsis.push(p.code)
            }
          }
        )
      }

      this.$store.dispatch(
        'surveillances/updateDatasetDenominators',
        {
          name: this.fields.name.value,
          mod: this.module,
          surv: this.surveillance.id,
          year: this.surveillance.year,
          den: denom,
          pneu: optional.pneumopathies,
          seps: optional.sepsis
        }).then(
        r => {
          const err = r.data.err
          if (!err) {
            vm.$notify(
              {
                type: 'success',
                title: 'Enregistrement des dénominateurs réussi !',
                content: `Les données «${this.fields.name.value}» sont
désormais complètes.`,
                duration: 4000
              }
            )
          }
        }
      )
    },

    updateSheets: function () {
      this.iterator.reset()
      this.iterator.start(this)
    },

    noSheets: function () {
      const vm = this
      vm.$store.dispatch(
        'surveillances/updateDatasetSheets',
        {
          name: vm.fields.name.value,
          mod: vm.module,
          shts: [],
          surv: vm.surveillance.id,
          year: vm.surveillance.year
        }).then(
        r => {
          const err = r.data.err
          if (!err) {
            vm.$notify(
              {
                type: 'success',
                title: 'Enregistrement réussi !',
                content: `Aucune infection n'a été déclarée sur la
période surveillée.`,
                duration: 4000
              }
            )
          }
        }
      )
    },

    save: function () {
      const f = this.fields
      const periods = this.periods

      this.waiting = true
      const vm = this
      this.$store.dispatch('surveillances/updateDataset', {
        name: f.name.value,
        mod: this.module,
        surv: this.surveillance.id,
        peri: periods,
        year: this.surveillance.year
      }).then(
        r => {
          const err = r.data.err
          if (err) {
            vm.waiting = false
            this.error.info.set(
              'Erreur interne !',
              `Une erreur inattendue s'est produite lors de l'enregistrement
du jeu de données.`,
              `Merci de vérifier votre connexion internet avant de réessayer.`
            )
          } else {
            vm.$notify(
              {
                type: 'success',
                title: 'Enregistrement réussi !',
                content: `Si vous avez modifié les dates, il vous faut mettre
à jour les fiches puis confirmer les dénominateurs.`,
                duration: 4000
              }
            )
          }
        }
      ).catch(
        e => {
          vm.waiting = false
          this.error.info.set(
            'Erreur interne !',
            `Une erreur inattendue s'est produite lors de l'enregistrement
du jeu de données.`,
            `Merci de vérifier votre connexion internet avant de réessayer.`
          )
        }
      )
    },

    checkName: function () {
      this.fields.name.error = ''
      if (this.fields.name === '') {
        this.fields.name.error =
          'Le nom du jeu de données ne doit pas être vide.'
      }
    },

    setPeriodsByUf: function (value) {
      this.fields.periodsByUfs = value
      this.fields.periods.length = 0
      const year = this.surveillance.year
      if (value === 1) {
        this.fields.periods.push(
          {
            value: {
              unit: '',
              twoPeriods: false,
              date: { start: `${year}-01-01`, end: `${year}-04-01`, error: '' },
              date2: { start: `${year}-01-01`, end: `${year}-04-01`, error: '' }
            }
          }
        )
      } else if (value === 2) {
        const periods = []
        const ufs = this.surveillanceUfList
        const uy = units[year]
        for (let i = 0; i < ufs.length; i++) {
          const u = ufs[i]
          const ufCode = uy.codeFromIndex(u.ufType)
          periods.push(
            {
              name: `${u.name} (${ufCode})`,
              value: {
                unit: `${u.ufType}.${u.name}`,
                twoPeriods: false,
                date: {
                  start: `${year}-01-01`, end: `${year}-04-01`, error: ''
                },
                date2: {
                  start: `${year}-01-01`, end: `${year}-04-01`, error: ''
                }
              }
            }
          )
        }
        this.fields.periods = periods
      }
    },

    getPeriodSheets: function () {
      const vm = this
      const list = []
      vm.periods.forEach(
        p => {
          list.push(
            vm.$store.dispatch(
              'sheet/fromPeriod',
              {
                type: vm.module,
                surv: vm.surveillance.id,
                year: vm.surveillance.year,
                peri: p
              }
            )
          )
        }
      )

      this.periodSheetsAsked = true
      Promise.all(list).then(
        (res) => {
          const sheets = []
          for (let i in res) {
            const r = res[i]
            if (r.data.err != null) {
              return res
            }
            sheets.push(...r.data.v)
          }

          const result = []
          const ids = {}
          sheets.forEach(
            s => {
              if (ids[s.id] == null) {
                result.push(s)
                ids[s.id] = true
              }
            }
          )
          vm.periodSheets = result

          return res
        }
      )
    },

    clean: function () {
      this.denInterDiscAlert = {}
      this.currentTab = 0
      this.ufCollapse = {}
      this.optCollapse = {}
      this.denInfo = {}
      this.totalDen = 0
      this.prg = { all: 0, miss: 0, alert: 0, completePercent: 0, alertPercent: 0 }
      this.noneMissingConfirmed = false
      this.dsDenominators = null
      this.sheetNumbers = false
      this.error.info.reset()
      this.error.denom.reset()
      this.error.sheets.reset()
      this.waiting = false
      this.iterator.reset()
      this.dsSheets = null
      this.periodSheets = null
      this.periodSheetsAsked = false
      this.emptyDiscList = []
      this.selectedUfList = []
      this.optional.seps = []
      this.optional.pneu = []
      this.fields.periodsByUfs = 0
      this.fields.periods.length = 0
    },

    fill: function () {
      this.clean()

      const { name } = this.fields
      name.error = ''
      name.value = this.name
      this.filled = this.module

      if (this.target == null) {
        this.setPeriodsByUf(1)
        return
      }

      this.fields.name.value = this.target.name

      const vm = this
      const arg = {
        mod: vm.module,
        surv: vm.surveillance.id,
        name: vm.fields.name.value,
        year: vm.surveillance.year
      }

      const promises = [
        vm.$store.dispatch('surveillances/getDatasetSheets', arg),
        vm.$store.dispatch('surveillances/getDatasetDenominators', arg)
      ]

      Promise.all(promises).then(
        (res) => {
          const sheets = res[0]
          const den = res[1]
          if (sheets.data.err != null) {
            vm.error.info.set(
              'Erreur interne !',
              `Une erreur inattendue s'est produite lors de la récupération
des fiches.`,
              `Merci de vérifier votre connexion internet avant de réessayer.`
            )
            return res
          }

          if (den.data.err != null) {
            vm.error.info.set(
              'Erreur interne !',
              `Une erreur inattendue s'est produite lors de la récupération
des dénominateurs.`,
              `Merci de vérifier votre connexion internet avant de réessayer.`
            )
            return res
          }
          vm.dsDenominators = den.data.den
          vm.dsSheets = sheets.data.v
          if (vm.dsDenominators != null) {
            if (vm.module === 'bactADI') {
              const keys = []
              const ufkeys = []
              const empty = []
              const selected = []
              vm.dsDenominators.forEach(
                d => {
                  if (d.uf === '__all__') {
                    if (keys.indexOf(d.ut) === -1) {
                      keys.push(d.ut)

                      const found = this.missingList.find(
                        (el) => el.disc.unitType === d.ut
                      )
                      if (found != null) {
                        empty.push(found.disc)
                      }
                    }
                  } else {
                    const key = `${d.ut}${d.uf}`
                    if (ufkeys.indexOf(key) === -1) {
                      ufkeys.push(key)
                      selected.push(
                        {
                          ut: d.ut,
                          uf: d.uf
                        }
                      )
                    }
                  }
                }
              )
              vm.emptyDiscList.push(...empty)
              vm.selectedUfList.push(...selected)
              this.noneMissingConfirmed = (vm.target.step === 2 && this.emptyDiscList.length === 0)
            } else if (vm.module === 'expADI') {
              vm.optionalList.forEach(
                op => {
                  const k = op.opt.key
                  const r = den.data[k]
                  const value = r != null && r.indexOf(op.code) !== -1
                  const sheets = vm.dsSheets.findIndex(
                    s =>
                      s.f.unitType === op.unit.unitType &&
                       s.f.uf === op.unit.uf &&
                       ((k === 'pneu' && s.f.expManagment === 2) ||
                        (k === 'seps' && s.f.expManagment === 3))
                  ) !== -1

                  vm.optional[k].push(
                    {
                      code: op.code,
                      value: value || sheets,
                      forced: sheets
                    }
                  )
                }
              )
            }
          }
          vm.currentTab = vm.target.step < 2 ? vm.target.step + 1 : 2
          return res
        }
      ).catch(
        e => {
          vm.error.info.set(
            'Erreur interne !',
            `Une erreur inattendue s'est produite lors de la récupération
des fiches et des dénominateurs.`,
            `Merci de vérifier votre connexion internet avant de réessayer.`
          )
        }
      )

      const p = vm.target.peri
      if (p.length > 0 && (p[0].unit == null || p[0].unit === '')) {
        vm.fields.periodsByUfs = 1
        vm.fields.periods.length = 0
        vm.fields.periods.push(
          { value: impl.buildPeriod(vm.surveillance.year, p, '') }
        )
        this.getPeriodSheets()
      }
    }
  }
}
</script>
<style lang="less" scoped>
@import '~variables';
.category-den-sep {
  border-top: 1px solid #0b89c0;
  width: 350px;
}

.discipline-selector {
  margin-bottom: 0.2rem;
}

.alert h4 {
  color: white;
}

.recap-list {
  list-style-type: none;
  .glyphicon {
    margin-right: 0.6rem;
  }
}

.button-list {
  margin-bottom: 1rem;

  .btn {
    margin-right: 1rem;
  }
}

.update-button {
  margin-top: 1.6rem;
}

.select-button {
  height: 44px;
}

.summary-col {
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  height: 100%;
}

.summary-bar {
  width: 100%;
  margin: auto;
  position: fixed;
  bottom: 0%;
  padding: 1rem;
  border-top: 0px solid;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  border-top-color: @modal-content-border-color;
  left: 0%;
  background-color: white;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
  z-index: 21000;
  height: 80px;

  .row {
    height: 100%;
  }
}

.summary-warning {
  background-color: lighten(@brand-warning, 30%);
}

.summary-success {
  background-color: lighten(@brand-success, 35%);
}

.main-modal {
  margin-bottom: 80px;
}

.well-danger {
  background-color: lighten(@brand-danger, 32%);
}
</style>
